import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  Box,
  Chip,
  Paper,
  MenuItem,
  FormHelperText,
  ClickAwayListener,
  Popper,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

export interface ChipOption {
  value: string
  label: string
}

interface ChipInputFieldProps {
  label: string
  required?: boolean
  error?: string
  options: ChipOption[]
  selectedValues: string[]
  onAdd: (value: string) => void
  onRemove: (value: string) => void
  onClear: () => void
  getChipLabel: (value: string) => string
  showAllOption?: boolean
  allOptionValue?: string
  allOptionLabel?: string
}

const ChipInputField: React.FC<ChipInputFieldProps> = ({
  label,
  required,
  error,
  options,
  selectedValues,
  onAdd,
  onRemove,
  onClear: _onClear,
  getChipLabel,
  showAllOption = false,
  allOptionValue = '',
  allOptionLabel = 'All',
}) => {
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const filteredOptions = options.filter(
    (opt) =>
      opt.label.toLowerCase().includes(inputValue.toLowerCase()) &&
      !selectedValues.includes(opt.value),
  )

  const showAll =
    showAllOption &&
    allOptionValue &&
    options.length > 1 &&
    !selectedValues.includes(allOptionValue) &&
    allOptionLabel.toLowerCase().includes(inputValue.toLowerCase())

  const totalDropdownItems = (showAll ? 1 : 0) + filteredOptions.length

  // Reset highlight when list changes
  useEffect(() => {
    setHighlightedIndex(-1)
  }, [inputValue, open])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('li')
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' })
      }
    }
  }, [highlightedIndex])

  const handleSelectOption = useCallback(
    (value: string) => {
      onAdd(value)
      setInputValue('')
      setHighlightedIndex(-1)
      inputRef.current?.focus()
    },
    [onAdd],
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && inputValue === '' && selectedValues.length > 0) {
      const lastVal = selectedValues[selectedValues.length - 1]
      onRemove(lastVal)
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) => Math.min(prev + 1, totalDropdownItems - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightedIndex >= 0) {
        if (showAll && highlightedIndex === 0) {
          handleSelectOption(allOptionValue)
        } else {
          const idx = showAll ? highlightedIndex - 1 : highlightedIndex
          if (filteredOptions[idx]) {
            handleSelectOption(filteredOptions[idx].value)
          }
        }
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
      setInputValue('')
    }
  }

  const handleContainerClick = () => {
    inputRef.current?.focus()
    setOpen(true)
  }

  const handleInputFocus = () => {
    setFocused(true)
    setOpen(true)
  }

  const handleInputBlur = () => {
    setFocused(false)
  }

  const hasSelections = selectedValues.length > 0
  const isLabelShrunk = focused || hasSelections || inputValue.length > 0

  return (
    <ClickAwayListener onClickAway={() => { setOpen(false); setInputValue('') }}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        {/* Field container mimicking MUI outlined input */}
        <Box
          ref={containerRef}
          onClick={handleContainerClick}
          sx={{
            position: 'relative',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            minHeight: '56px',
            padding: '8px 40px 8px 14px',
            border: error
              ? '1px solid #d32f2f'
              : focused
                ? '2px solid #1976d2'
                : '1px solid rgba(0,0,0,0.23)',
            borderRadius: '4px',
            cursor: 'text',
            backgroundColor: 'background.paper',
            boxSizing: 'border-box',
            '&:hover': {
              border: error
                ? '1px solid #d32f2f'
                : focused
                  ? '2px solid #1976d2'
                  : '1px solid rgba(0,0,0,0.87)',
            },
          }}
        >
          {/* Floating label — matches theme allVariants fontSize (0.75rem) */}
          <Box
            component='label'
            sx={{
              position: 'absolute',
              left: isLabelShrunk ? '10px' : '14px',
              top: isLabelShrunk ? '-9px' : '50%',
              transform: isLabelShrunk ? 'none' : 'translateY(-50%)',
              fontSize: isLabelShrunk ? '0.5625rem' : '0.75rem',
              lineHeight: 1,
              fontFamily: 'inherit',
              fontWeight: 400,
              letterSpacing: '0.00938em',
              color: error
                ? '#d32f2f'
                : focused
                  ? '#1976d2'
                  : 'rgba(0,0,0,0.6)',
              backgroundColor: 'background.paper',
              px: isLabelShrunk ? '4px' : 0,
              pointerEvents: 'none',
              transition: 'all 0.15s ease',
              whiteSpace: 'nowrap',
              zIndex: 1,
              display: 'block',
            }}
          >
            {label}{required && ' *'}
          </Box>

          {/* Chips */}
          {selectedValues.map((val) => (
            <Chip
              key={val}
              label={getChipLabel(val)}
              size='small'
              onDelete={(e) => {
                e.stopPropagation()
                onRemove(val)
              }}
              onMouseDown={(e) => e.stopPropagation()}
              deleteIcon={<ClearIcon />}
              sx={{
                height: '24px',
                '& .MuiChip-deleteIcon': {
                  cursor: 'pointer',
                  '&:hover': { color: 'error.main' },
                },
              }}
            />
          ))}

          {/* Inline input */}
          <input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value)
              setOpen(true)
            }}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            placeholder=''
            style={{
              flex: '1 1 80px',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              font: 'inherit',
              color: 'inherit',
              padding: '2px 0',
              minWidth: '80px',
            }}
          />

          {/* Dropdown arrow icon */}
          <ArrowDropDownIcon
            sx={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
              transition: 'transform 0.2s',
              color: focused ? '#1976d2' : 'text.secondary',
              pointerEvents: 'none',
            }}
          />
        </Box>

        {/* Error helper text */}
        {error && (
          <FormHelperText error sx={{ mx: '14px' }}>
            {error}
          </FormHelperText>
        )}

        {/* Dropdown */}
        <Popper
          open={open && (showAll ? true : filteredOptions.length > 0)}
          anchorEl={containerRef.current}
          placement='bottom-start'
          style={{ zIndex: 1400, width: containerRef.current?.offsetWidth ?? 'auto' }}
          modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
        >
          <Paper
            elevation={4}
            sx={{
              width: '100%',
              maxHeight: '280px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              component='ul'
              ref={listRef}
              sx={{
                listStyle: 'none',
                m: 0,
                p: 0,
                overflowY: 'auto',
                maxHeight: '280px',
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '3px' },
              }}
            >
              {showAll && (
                <MenuItem
                  component='li'
                  selected={highlightedIndex === 0}
                  onMouseDown={(e: React.MouseEvent) => {
                    e.preventDefault()
                    handleSelectOption(allOptionValue)
                  }}
                  onMouseEnter={() => setHighlightedIndex(0)}
                  sx={{ fontStyle: 'italic' }}
                >
                  {allOptionLabel}
                </MenuItem>
              )}
              {filteredOptions.length === 0 && !showAll && (
                <Box sx={{ p: 2, color: 'text.secondary', fontSize: '14px' }}>No options found</Box>
              )}
              {filteredOptions.map((opt, idx) => {
                const itemIndex = showAll ? idx + 1 : idx
                return (
                  <MenuItem
                    component='li'
                    key={opt.value}
                    selected={highlightedIndex === itemIndex}
                    onMouseDown={(e: React.MouseEvent) => {
                      e.preventDefault()
                      handleSelectOption(opt.value)
                    }}
                    onMouseEnter={() => setHighlightedIndex(itemIndex)}
                  >
                    {opt.label}
                  </MenuItem>
                )
              })}
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}

export default ChipInputField
